import { EMPTY_PLACEHOLDER } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import { DocumentList } from './DocumentList';
import { EUROPACE_PROCESS_EVENTS_DISCRIMINATORS } from '~/constants';
import type {
    SchemaEuropaceEventsTableData,
    SchemaEuropaceProcessOfferEventData,
} from '~/features/leadDetails/components/LeadSideInfo/ExternalServices/schemas/europaceProcessEventsData';
import { dateTimeTwoDigitFormat, timeFormat } from '~/lib/i18n';

type Props = {
    payload: SchemaEuropaceEventsTableData;
};

const isOfferEventTG = (payload: SchemaEuropaceEventsTableData): payload is SchemaEuropaceProcessOfferEventData => {
    return payload.discriminator === EUROPACE_PROCESS_EVENTS_DISCRIMINATORS.OFFER;
};

/** One label/value row of the event card, matching the "Label:" + value layout from the design. */
const EventRow = ({ children, label }: { label: string; children: React.ReactNode }) => (
    <div className="flex gap-2">
        <span className="w-36 shrink-0 font-medium text-midnight">{label}</span>
        <span className="flex-1 text-midnight">{children}</span>
    </div>
);

export const EuropaceEventCard: React.FC<Props> = ({ payload }) => {
    const { i18n, t } = useTranslation();
    const isOfferEvent = isOfferEventTG(payload);

    let typeTranslation = `SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.TYPE.${payload.type}`;
    if (i18n.exists(typeTranslation)) {
        // TODO: add a ts check that the translation exists
        // @ts-expect-error this is a workaround for the i18n type check
        typeTranslation = t(typeTranslation);
    } else {
        typeTranslation = payload.type ?? EMPTY_PLACEHOLDER;
    }

    return (
        <div className="flex flex-col gap-3 rounded-md border border-grey-stroke bg-white p-6 text-sm">
            <EventRow label={t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.LABEL_DATE')}>
                {payload.date
                    ? t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.DATE_TIME', {
                          date: dateTimeTwoDigitFormat(new Date(payload.date)),
                          time: timeFormat(new Date(payload.date)),
                      })
                    : EMPTY_PLACEHOLDER}
            </EventRow>
            <EventRow label={t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.LABEL_TYPE')}>
                {typeTranslation}
            </EventRow>
            <EventRow label={t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.LABEL_TEXT')}>
                {payload.text ?? EMPTY_PLACEHOLDER}
            </EventRow>
            <EventRow label={t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.LABEL_PRODUCT_PROVIDER')}>
                {isOfferEvent && payload.loanProvider?.name ? payload.loanProvider.name : EMPTY_PLACEHOLDER}
            </EventRow>
            <EventRow label={t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.LABEL_APPLICATION_NUMBER')}>
                {isOfferEvent && payload.europaceOfferId ? payload.europaceOfferId : EMPTY_PLACEHOLDER}
            </EventRow>
            <EventRow label={t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.LABEL_DOCUMENTS')}>
                <DocumentList
                    documents={payload.documents}
                    links={'links' in payload ? payload.links : undefined}
                    europaceOfferId={isOfferEvent ? payload.europaceOfferId : undefined}
                />
            </EventRow>
        </div>
    );
};
